// stores/CampaignStore.ts
import { toast } from 'react-toastify';

import { TFunction } from 'i18next';
import { makeAutoObservable, runInAction } from 'mobx';

import { CampaignService } from '../services/CampaignService';
import { Campaign } from '../services/types/api.generated';

enum CampaignActiveStatus {
  Inactive = 0,
  Active = 1
}

class CampaignStore {
  campaigns: Campaign[] = [];

  loading = false;

  error = false;

  constructor() {
    makeAutoObservable(this);
  }

  clearCampaigns() {
    this.campaigns = [];
  }

  async fetchCampaigns(organizationId: number | undefined | null, t: TFunction) {
    this.loading = true;
    this.error = false;

    try {
      if (!organizationId) {
        // Clear campaigns and show a helpful message
        runInAction(() => {
          this.campaigns = [];
          this.error = true;
        });
        toast.error(
          t('campaigns.no_organization_selected', 'Please select or create an organization to view campaigns.')
        );
        return;
      }

      const data = await CampaignService.getCampaigns(organizationId);
      runInAction(() => {
        this.campaigns = data;
      });
    } catch (err) {
      runInAction(() => {
        this.error = true;
      });
      toast.error(t('campaigns.loading_error'));
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async startCampaign(campaignId: string, t: TFunction) {
    try {
      const updated = await CampaignService.updateCampaignStatus(campaignId, CampaignActiveStatus.Active);
      if (!updated) {
        toast.error(t('campaigns.errors.campaign_status_update_failed'));
        return;
      }
      runInAction(() => this.updateCampaign(updated));
      toast.success(t('campaigns.campaign_status_active'));
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('campaigns.errors.campaign_status_update_failed');
      toast.error(message);
    }
  }

  async stopCampaign(campaignId: string, t: TFunction) {
    try {
      const updated = await CampaignService.updateCampaignStatus(campaignId, CampaignActiveStatus.Inactive);
      if (!updated) {
        toast.error(t('campaigns.errors.campaign_status_update_failed'));
        return;
      }
      runInAction(() => this.updateCampaign(updated));
      toast.success(t('campaigns.campaign_status_inactive'));
    } catch {
      toast.error(t('campaigns.errors.campaign_status_update_failed'));
    }
  }

  updateCampaign(updated: Campaign) {
    const index = this.campaigns.findIndex((c) => c.id === updated.id);
    if (index >= 0) {
      this.campaigns[index] = updated;
    } else {
      this.campaigns = [...this.campaigns, updated];
    }
  }
}

const campaignStore = new CampaignStore();
export default campaignStore;
